import type { SampleFile } from '@mollusk/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

export interface SampleData {
  generated_at: string | null;
  files: Omit<SampleFile, 'object'>[];
  checks?: { key: string; label: string }[];
  removed?: Record<string, { in_scope: number; removed: number; checks: Record<string, number> }>;
}

const SAMPLES = [
  {
    key: 'arizona',
    title: 'Arizona, last 10 years',
    text: 'Every permit issued in the last 10 years in the Arizona jurisdictions we cover, untagged permits included, so the state is complete.',
  },
  {
    key: 'random10k',
    title: 'Random 10,000',
    text: 'A representative random draw of 10,000 tagged permits from across the country, with no catch-all type lists.',
  },
] as const;

const mb = (bytes: number | null) =>
  bytes == null ? '' : ` · ${(bytes / 1024 / 1024).toFixed(1)} MB`;
const href = (f: { key: string; format: string }) => `/api/sample/download/${f.key}/${f.format}`;

/** The Sample data page (#173). */
export function SampleDataView({ data }: { data: SampleData }) {
  const dictionary = data.files.find((f) => f.key === 'dictionary');
  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
        Sample data
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Downloads are for evaluation only, under the terms you accepted.
      </Alert>
      {data.generated_at ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Built {data.generated_at.slice(0, 10)}, after the latest weekly load. Columns follow the
          data dictionary.
        </Typography>
      ) : (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          The sample files are built after the next weekly load.
        </Typography>
      )}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        {SAMPLES.map((s) => {
          const files = data.files.filter((f) => f.key === s.key);
          const rows = files[0]?.rows;
          return (
            <Paper key={s.key} data-testid={`sample-${s.key}`} sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 600 }}>{s.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {s.text}
                {rows != null ? ` ${rows.toLocaleString()} permits.` : ''}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {files.map((f) => (
                  <Button key={f.format} variant="outlined" href={href(f)} download={f.filename}>
                    {f.format === 'csv' ? 'CSV' : 'Parquet'}
                    {mb(f.bytes)}
                  </Button>
                ))}
                {files.length === 0 ? (
                  <Typography color="text.secondary">Not built yet.</Typography>
                ) : null}
              </Box>
            </Paper>
          );
        })}
      </Box>
      {dictionary ? (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography sx={{ fontWeight: 600 }}>Data dictionary</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Every field and tag, as an Excel workbook.
          </Typography>
          <Button variant="outlined" href={href(dictionary)} download={dictionary.filename}>
            Data dictionary (.xlsx)
          </Button>
        </Paper>
      ) : null}
      {data.removed && data.checks ? (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Rows left out (admins only)</Typography>
          <Table size="small" aria-label="Rows removed by each check">
            <TableHead>
              <TableRow>
                <TableCell>Check</TableCell>
                {SAMPLES.map((s) => (
                  <TableCell key={s.key} align="right">
                    {s.title}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.checks.map((c) => (
                <TableRow key={c.key}>
                  <TableCell>{c.label}</TableCell>
                  {SAMPLES.map((s) => (
                    <TableCell key={s.key} align="right">
                      {(data.removed?.[s.key]?.checks[c.key] ?? 0).toLocaleString()}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Removed (any check) / in scope</TableCell>
                {SAMPLES.map((s) => {
                  const r = data.removed?.[s.key];
                  return (
                    <TableCell key={s.key} align="right" sx={{ fontWeight: 600 }}>
                      {r ? `${r.removed.toLocaleString()} / ${r.in_scope.toLocaleString()}` : '—'}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary">
            A row can fail more than one check. The random sample is drawn after the checks, so it
            stays at 10,000.
          </Typography>
        </Paper>
      ) : null}
    </Box>
  );
}
